/**
 * Trend Booster Dashboard Overlay
 *
 * Replicates the AmiBroker AFL GFX dashboard panel:
 *   Signal, Trend, Entry, SL, Target1, Target2, Live P/L, Booked P/L,
 *   Script name, Price, and target/SL hit status.
 *
 * Features: toggle collapse, arrow badges, TP/SL hit markers on chart.
 */
import React from "react";
import { AlphaTradeDashboard } from "../../indicators/built-in/alpha-trade";
import "./AlphaTradeOverlay.scss";

interface ArrowBadge {
  x: number;
  y: number;
  direction: "long" | "short";
  type: string;
  position: string;
}

interface AlphaTradeOverlayProps {
  dashboard: AlphaTradeDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard: boolean;
  onToggleDashboard: () => void;
  arrowBadges: ArrowBadge[];
}

const AlphaTradeOverlay: React.FC<AlphaTradeOverlayProps> = ({
  dashboard,
  symbol,
  visible,
  showDashboard,
  onToggleDashboard,
  arrowBadges,
}) => {
  if (!visible) return null;

  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "-");
  const fmtPL = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;

  // Arrow badges rendered on the chart
  const renderBadges = () => (
    <>
      {arrowBadges.map((b, i) => {
        if (b.type === "signal") {
          const isBuy = b.direction === "long";
          const label = isBuy ? "Buy" : "Sell";
          const color = isBuy ? "#26a69a" : "#ef5350";
          const top = b.position === "below" ? b.y + 18 : b.y - 38;
          return (
            <div
              key={`at-sig-${i}`}
              className="at-badge"
              style={{
                left: b.x - 20,
                top,
                color,
                borderColor: color,
              }}
            >
              {label}
            </div>
          );
        }
        if (b.type === "tp1" || b.type === "tp2") {
          const label = b.type === "tp1" ? "TGT-1 ✓" : "TGT-2 ✓";
          const top = b.position === "above" ? b.y - 38 : b.y + 18;
          return (
            <div
              key={`at-tp-${i}`}
              className="at-badge at-badge--tp"
              style={{ left: b.x - 24, top }}
            >
              {label}
            </div>
          );
        }
        if (b.type === "sl") {
          const top = b.position === "above" ? b.y - 38 : b.y + 18;
          return (
            <div
              key={`at-sl-${i}`}
              className="at-badge at-badge--sl"
              style={{ left: b.x - 20, top }}
            >
              SL ✗
            </div>
          );
        }
        return null;
      })}
    </>
  );

  // Glow class based on signal
  const glowClass = dashboard?.signal === "BUY" ? "at-glow-buy" : dashboard?.signal === "SELL" ? "at-glow-sell" : "";

  // Collapsed state
  if (!showDashboard) {
    return (
      <div className={`alpha-trade-overlay at-collapsed ${glowClass}`}>
        <span className="at-collapsed-title">Trend Booster</span>
        <button className="at-toggle-btn at-toggle-btn--right" onClick={onToggleDashboard}>▼</button>
        {renderBadges()}
      </div>
    );
  }

  if (!dashboard) return <>{renderBadges()}</>;

  const {
    signal,
    trend,
    entry,
    stopLoss,
    target1,
    target2,
    currentPrice,
    livePL,
    bookedPL,
    status,
    tp1Hit,
    tp2Hit,
    slHit,
  } = dashboard;

  const signalColor =
    signal === "BUY" ? "#26a69a" : signal === "SELL" ? "#ef5350" : "#8b949e";

  const statusClass = status === "Active" ? "active" : slHit ? "sl-hit" : "target-hit";

  return (
    <>
      <div className={`alpha-trade-overlay ${glowClass}`}>
        {/* Header */}
        <div className="alpha-header">
          <span className="alpha-title">Trend Booster</span>
          <span className={`alpha-status ${statusClass}`}>{status}</span>
          <button className="at-toggle-btn at-toggle-btn--right" onClick={onToggleDashboard}>▲</button>
        </div>

        {/* Body */}
        <div className="alpha-body">
          {/* Row 1: Signal & Trend */}
          <div className="alpha-row">
            <div className="alpha-cell">
              <span className="alpha-label">SIGNAL</span>
              <span className="alpha-value alpha-value-box" style={{ color: signalColor }}>{signal || "-"}</span>
            </div>
            <div className="alpha-cell">
              <span className="alpha-label">TREND</span>
              <span className={`alpha-value alpha-value-box at-trend-${trend.toLowerCase()}`}>{trend}</span>
            </div>
          </div>

          {/* Row 2: Entry & Stop */}
          <div className="alpha-row">
            <div className="alpha-cell">
              <span className="alpha-label">ENTRY</span>
              <span className="alpha-value alpha-value-box">{fmt(entry)}</span>
            </div>
            <div className="alpha-cell">
              <span className="alpha-label">STOP</span>
              <span className={`alpha-value alpha-value-box ${slHit ? "alpha-hit" : ""}`}>{fmt(stopLoss)}</span>
            </div>
          </div>

          {/* Row 3: Targets */}
          <div className="alpha-row">
            <div className="alpha-cell">
              <span className="alpha-label">TARGET 1</span>
              <span className={`alpha-value alpha-value-box ${tp1Hit ? "alpha-hit" : ""}`}>{fmt(target1)}</span>
            </div>
            <div className="alpha-cell">
              <span className="alpha-label">TARGET 2</span>
              <span className={`alpha-value alpha-value-box ${tp2Hit ? "alpha-hit" : ""}`}>{fmt(target2)}</span>
            </div>
          </div>

          {/* Row 4: P/L */}
          <div className="alpha-row">
            <div className="alpha-cell">
              <span className="alpha-label">LIVE P/L</span>
              <span className={`alpha-value alpha-value-box ${livePL >= 0 ? "alpha-profit" : "alpha-loss"}`}>{fmtPL(livePL)}</span>
            </div>
            <div className="alpha-cell">
              <span className="alpha-label">BOOKED P/L</span>
              <span className={`alpha-value alpha-value-box ${bookedPL >= 0 ? "alpha-profit" : "alpha-loss"}`}>{fmtPL(bookedPL)}</span>
            </div>
          </div>

          {/* Footer: Symbol & Price */}
          <div className="alpha-footer">
            <div className="alpha-footer-row">
              <span className="alpha-footer-label">SCRIPT</span>
              <span className="alpha-footer-label">PRICE</span>
            </div>
            <div className="alpha-footer-row alpha-footer-values">
              <span>{symbol}</span>
              <span>{currentPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Arrow badges rendered over chart */}
      {renderBadges()}
    </>
  );
};

export default AlphaTradeOverlay;
